/**
 * Header Logo Scroll Swapping
 * Swaps header logo based on scroll position:
 * - State 1: Default logo (at top, before first hero)
 * - State 2: After scrolling past first hero section (Belfast icon)
 * - State 3: After scrolling past third section
 * - State 4: After scrolling past fourth section
 */

(function() {
  'use strict';

  let headerLogo = null;
  let firstHero = null;
  let hasScrollLogo2 = false;
  let hasScrollLogo3 = false;
  let hasScrollLogo4 = false;
  let currentState = 1;
  let thresholds = { firstHeroBottom: 0, thirdSectionBottom: Infinity, fourthSectionBottom: Infinity };

  // Initialize the logo scroll functionality
  function init() {
    // Target the far left heading logo instead of the center logo
    headerLogo = document.querySelector('.header-heading-logo');
    if (!headerLogo) {
      console.warn('Header logo scroll: .header-heading-logo element not found');
      return;
    }

    // Check if scroll logos are configured
    hasScrollLogo2 = !!headerLogo.querySelector('.header-heading-logo__image-container--state-2');
    hasScrollLogo3 = !!headerLogo.querySelector('.header-heading-logo__image-container--state-3');
    hasScrollLogo4 = !!headerLogo.querySelector('.header-heading-logo__image-container--state-4');
    
    if (!hasScrollLogo2 && !hasScrollLogo3 && !hasScrollLogo4) {
      // No scroll logos configured, default to state 1
      headerLogo.setAttribute('data-active-logo-state', '1');
      return;
    }

    // Find first hero section - prioritize .hm-hero, then .hero, then any element with 'hero' in class
    firstHero = document.querySelector('.hm-hero') || 
                document.querySelector('.hero') || 
                document.querySelector('[class*="hero"]');
    
    if (!firstHero) {
      // If no hero section, default to state 1
      headerLogo.setAttribute('data-active-logo-state', '1');
      return;
    }

    // Wait a bit for content to settle, then calculate thresholds
    setTimeout(() => {
      calculateThresholds();
      updateLogoState(window.scrollY || document.documentElement.scrollTop || 0);
    }, 100);

    // Also recalculate after images load
    if (document.readyState === 'complete') {
      calculateThresholds();
    } else {
      window.addEventListener('load', () => {
        calculateThresholds();
        updateLogoState(window.scrollY || document.documentElement.scrollTop || 0);
      });
    }
  }

  // Calculate scroll thresholds based on hero and section positions
  function calculateThresholds() {
    if (!firstHero) return;

    // Get the hero section's bottom position
    const firstHeroRect = firstHero.getBoundingClientRect();
    const firstHeroBottom = window.scrollY + firstHeroRect.bottom;
    
    // Find all Shopify sections
    const allSections = Array.from(document.querySelectorAll('.shopify-section'));
    
    // Find which section contains the hero
    let firstHeroSection = null;
    let firstHeroIndex = -1;
    
    for (let i = 0; i < allSections.length; i++) {
      const section = allSections[i];
      if (section.contains(firstHero) || section === firstHero) {
        firstHeroSection = section;
        firstHeroIndex = i;
        break;
      }
    }
    
    // If hero is not in a .shopify-section, try to find it directly
    if (firstHeroIndex === -1 && firstHero.closest('.shopify-section')) {
      firstHeroSection = firstHero.closest('.shopify-section');
      firstHeroIndex = allSections.indexOf(firstHeroSection);
    }
    
    // Calculate third section threshold
    let thirdSectionBottom = Infinity;
    
    if (firstHeroIndex >= 0 && allSections.length > firstHeroIndex + 2) {
      // Get the third section (2 sections after the hero)
      const thirdSection = allSections[firstHeroIndex + 2];
      if (thirdSection) {
        const thirdSectionRect = thirdSection.getBoundingClientRect();
        thirdSectionBottom = window.scrollY + thirdSectionRect.bottom;
      }
    }
    
    // Fallback: if we can't find the third section, use a calculated distance
    if (thirdSectionBottom === Infinity) {
      thirdSectionBottom = firstHeroBottom + (firstHeroRect.height * 2);
    }
    
    // Calculate fourth section threshold
    let fourthSectionBottom = Infinity;
    
    if (firstHeroIndex >= 0 && allSections.length > firstHeroIndex + 3) {
      // Get the fourth section (3 sections after the hero)
      const fourthSection = allSections[firstHeroIndex + 3];
      if (fourthSection) {
        const fourthSectionRect = fourthSection.getBoundingClientRect();
        fourthSectionBottom = window.scrollY + fourthSectionRect.bottom;
      }
    }
    
    // Fallback: if we can't find the fourth section, use a calculated distance
    if (fourthSectionBottom === Infinity) {
      fourthSectionBottom = thirdSectionBottom + (firstHeroRect.height * 1.5);
    }
    
    thresholds = {
      firstHeroBottom: Math.max(0, firstHeroBottom),
      thirdSectionBottom: Math.max(firstHeroBottom, thirdSectionBottom),
      fourthSectionBottom: Math.max(thirdSectionBottom, fourthSectionBottom)
    };
  }

  // Update logo state based on scroll position
  function updateLogoState(scrollTop) {
    if (!headerLogo) return;

    let newState = 1;

    // Determine logo state based on scroll position
    // Check from bottom to top to handle edge cases correctly
    if (hasScrollLogo4 && scrollTop >= thresholds.fourthSectionBottom) {
      // Past fourth section - show logo state 4
      newState = 4;
    } else if (hasScrollLogo3 && scrollTop >= thresholds.thirdSectionBottom) {
      // Past third section - show logo state 3
      newState = 3;
    } else if (hasScrollLogo2 && scrollTop >= thresholds.firstHeroBottom) {
      // Past first hero - show logo state 2
      newState = 2;
    } else {
      // At top or before first hero - show logo state 1
      newState = 1;
    }

    // Only update if state changed
    if (newState !== currentState) {
      currentState = newState;
      headerLogo.setAttribute('data-active-logo-state', currentState.toString());
    }
  }

  // Handle scroll events with throttling
  let ticking = false;
  function handleScroll() {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
        updateLogoState(scrollTop);
        ticking = false;
      });
      ticking = true;
    }
  }

  // Recalculate thresholds on resize
  let resizeTimeout;
  function handleResize() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      calculateThresholds();
      updateLogoState(window.scrollY || document.documentElement.scrollTop || 0);
    }, 150);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM is already ready
    init();
  }

  // Set up event listeners
  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('resize', handleResize, { passive: true });
  
  // Recalculate on orientation change
  window.addEventListener('orientationchange', () => {
    setTimeout(() => {
      calculateThresholds();
      updateLogoState(window.scrollY || document.documentElement.scrollTop || 0);
    }, 300);
  }, { passive: true });
})();

